import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ModelCombobox } from "@/features/text/ModelCombobox";
import { cn } from "@/lib/utils";
import { formatDuration, type ModelInfo } from "@/lib/playground";

export type PanelMessage = {
  role: "user" | "assistant";
  content: string;
};

export type TextPanelState = {
  id: string;
  model: string;
  messages: PanelMessage[];
  streaming: boolean;
  error: string | null;
  stats: { durationMs: number; totalTokens: number | null } | null;
};

type ChatColumnProps = {
  panel: TextPanelState;
  models: ModelInfo[];
  onModelChange: (id: string) => void;
  onRemove: () => void;
  canRemove: boolean;
};

// Cell of a collapsed hairline grid; the container owns the top/left edges.
const cellClass =
  "relative flex h-[480px] flex-col border-0 border-b border-r " +
  "after:absolute after:left-0 after:top-0 after:z-[1] after:h-[2px] after:w-0 " +
  "after:bg-primary after:transition-[width] after:duration-300 after:content-[''] " +
  "hover:after:w-full";

export const ChatColumn = ({
  panel,
  models,
  onModelChange,
  onRemove,
  canRemove,
}: ChatColumnProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [panel.messages]);

  return (
    <Card className={cellClass}>
      <CardHeader className="flex flex-row items-center gap-1 space-y-0 border-b p-2">
        <ModelCombobox
          models={models}
          value={panel.model}
          onChange={onModelChange}
          disabled={panel.streaming}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={!canRemove || panel.streaming}
          aria-label="Remove model"
          className="h-8 w-8 px-0 text-subtle hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>

      <CardContent
        ref={scrollRef}
        className="flex-1 space-y-4 overflow-y-auto p-4"
      >
        {panel.messages.length === 0 ? (
          <p className="pt-10 text-center font-mono text-[11px] uppercase tracking-[0.8px] text-subtle">
            [ awaiting first run ]
          </p>
        ) : (
          panel.messages.map((message, index) =>
            message.role === "user" ? (
              <div
                key={index}
                className="whitespace-pre-wrap border-l-2 border-primary py-0.5 pl-3 text-sm font-medium leading-relaxed text-foreground"
              >
                {message.content}
              </div>
            ) : (
              <div
                key={index}
                className="border-l-2 border-border py-0.5 pl-3 text-sm leading-relaxed text-foreground/85"
              >
                <div className="markdown">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </Markdown>
                </div>
                {panel.streaming && index === panel.messages.length - 1 ? (
                  <span className="mt-1 inline-block h-3.5 w-1.5 animate-pulse bg-primary" />
                ) : null}
              </div>
            )
          )
        )}
        {panel.error ? (
          <p className="border border-primary bg-primary/5 px-3 py-2 text-sm text-primary">
            {panel.error}
          </p>
        ) : null}
      </CardContent>

      <div className="flex items-center gap-2 border-t px-4 py-2 font-mono text-[10px] uppercase tracking-[0.8px] text-subtle">
        {panel.streaming ? (
          <>
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success shadow-[0_0_8px_rgba(34,197,94,0.6)]" />
            Streaming
          </>
        ) : panel.stats ? (
          `${formatDuration(panel.stats.durationMs)}${
            panel.stats.totalTokens !== null
              ? ` · ${panel.stats.totalTokens} tok`
              : ""
          }`
        ) : (
          "— no run yet"
        )}
      </div>
    </Card>
  );
};
